import { useCallback } from "react";

import { STATUS_PRIORITY } from "./mediaStreamUtils";
import type {
  MediaInputDevice,
  PermissionState,
  StreamStatus,
} from "./mediaTypes";
import { useCameraStream, type StartCameraOptions } from "./useCameraStream";
import { useScreenStream } from "./useScreenStream";

interface UseMediaStreamsReturn {
  cameraDevices: MediaInputDevice[];
  cameraStream: MediaStream | null;
  cleanup: () => void;
  error: Error | null;
  isCameraEnabled: boolean;
  isMicEnabled: boolean;
  isScreenSharing: boolean;
  micDevices: MediaInputDevice[];
  permissionState: PermissionState;
  screenStream: MediaStream | null;
  startCamera: (options?: StartCameraOptions) => Promise<void>;
  startScreenShare: () => Promise<void>;
  status: StreamStatus;
  stopCamera: () => void;
  stopScreenShare: () => void;
  toggleCamera: () => void;
  toggleMic: () => void;
}

export const useMediaStreams = (): UseMediaStreamsReturn => {
  const camera = useCameraStream();
  const screen = useScreenStream();
  const cleanupCamera = camera.cleanup;
  const cleanupScreen = screen.cleanup;

  const status =
    STATUS_PRIORITY.find(s => [camera.status, screen.status].includes(s)) ??
    "idle";

  const cleanup = useCallback(() => {
    cleanupScreen();
    cleanupCamera();
  }, [cleanupCamera, cleanupScreen]);

  return {
    cameraDevices: camera.cameraDevices,
    cameraStream: camera.cameraStream,
    cleanup,
    error: screen.error ?? camera.error,
    isCameraEnabled: camera.isCameraEnabled,
    isMicEnabled: camera.isMicEnabled,
    isScreenSharing: screen.isScreenSharing,
    micDevices: camera.micDevices,
    permissionState: camera.permissionState,
    screenStream: screen.screenStream,
    startCamera: camera.startCamera,
    startScreenShare: screen.startScreenShare,
    status,
    stopCamera: camera.stopCamera,
    stopScreenShare: screen.stopScreenShare,
    toggleCamera: camera.toggleCamera,
    toggleMic: camera.toggleMic,
  };
};
